import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getEnvironmentRoutes } from '../../api/adminApi.js';
import {
  executeInfluencerImport,
  getInfluencerImportBatch,
  listInfluencerImportBatches,
  previewInfluencerImport,
} from '../../api/influencerImportApi.js';
import type {
  EnvironmentRouteAccount,
  InfluencerFriendMapping,
  InfluencerImportBatch,
  InfluencerImportBatchDetail,
  InfluencerImportPreview,
  InfluencerMappingStatus,
  InfluencerPreviewRow,
} from '../../api/types.js';
import '../../styles/influencer-imports.css';

interface InfluencerImportConsoleProps {
  onToast: (message: string, type?: 'info' | 'success' | 'error') => void;
}

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const HISTORY_LIMIT = 20;
const XLSX_ACCEPT = '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export function InfluencerImportConsole({ onToast }: InfluencerImportConsoleProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [bots, setBots] = useState<EnvironmentRouteAccount[]>([]);
  const [selectedBotId, setSelectedBotId] = useState<number | null>(null);
  const [message, setMessage] = useState('你好，我是品牌合作运营，想和你沟通一下合作细节。');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<InfluencerImportPreview | null>(null);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [history, setHistory] = useState<InfluencerImportBatch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [selectedBatchDetail, setSelectedBatchDetail] = useState<InfluencerImportBatchDetail | null>(null);
  const [booting, setBooting] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const availableBots = useMemo(
    () => bots,
    [bots],
  );

  const selectedBot = useMemo(
    () => availableBots.find((bot) => bot.wechatRecordId === selectedBotId) ?? null,
    [availableBots, selectedBotId],
  );

  const pendingWechatIds = useMemo(
    () => unique((preview?.rows ?? [])
      .filter((row) => row.errors.length === 0 && !row.friendId)
      .map((row) => row.wechatId)),
    [preview],
  );

  const executeBlockedReason = useMemo(() => {
    if (!preview) return '先上传 Excel 并生成预览。';
    if (!message.trim()) return '先填写验证语。';
    if (!selectedBotId) return '先选择目标工作号。';
    if (preview.botWechatRecordId !== selectedBotId) return '目标工作号已变更，请重新预览。';
    return null;
  }, [message, preview, selectedBotId]);

  const loadDetail = useCallback(async (batchId: number, background = false) => {
    if (!background) setLoadingDetail(true);
    try {
      const detail = await getInfluencerImportBatch(batchId);
      setSelectedBatchDetail(detail);
      return detail;
    } catch (error) {
      onToast(error instanceof Error ? error.message : '批次详情加载失败', 'error');
      if (!background) setSelectedBatchDetail(null);
      return null;
    } finally {
      if (!background) setLoadingDetail(false);
    }
  }, [onToast]);

  const loadHistory = useCallback(async (preferredBatchId?: number | null) => {
    setLoadingHistory(true);
    try {
      const response = await listInfluencerImportBatches(HISTORY_LIMIT);
      setHistory(response.batches);
      const nextSelectedBatchId = preferredBatchId
        ?? (selectedBatchId && response.batches.some((batch) => batch.id === selectedBatchId)
          ? selectedBatchId
          : response.batches[0]?.id ?? null);
      setSelectedBatchId(nextSelectedBatchId);
      if (nextSelectedBatchId) {
        await loadDetail(nextSelectedBatchId, preferredBatchId === nextSelectedBatchId);
      } else {
        setSelectedBatchDetail(null);
      }
    } catch (error) {
      onToast(error instanceof Error ? error.message : '导入历史加载失败', 'error');
    } finally {
      setLoadingHistory(false);
    }
  }, [loadDetail, onToast, selectedBatchId]);

  const loadInitial = useCallback(async () => {
    setBooting(true);
    try {
      const [botResponse, historyResponse] = await Promise.all([
        getEnvironmentRoutes(),
        listInfluencerImportBatches(HISTORY_LIMIT),
      ]);
      const nextBots = botResponse.accounts.filter((bot) => bot.bridgeAllowed);
      setBots(nextBots);
      setSelectedBotId((current) => current ?? nextBots[0]?.wechatRecordId ?? null);
      setHistory(historyResponse.batches);
      const firstBatchId = historyResponse.batches[0]?.id ?? null;
      setSelectedBatchId(firstBatchId);
      if (firstBatchId) {
        await loadDetail(firstBatchId);
      }
    } catch (error) {
      onToast(error instanceof Error ? error.message : '达人导入页面加载失败', 'error');
    } finally {
      setBooting(false);
    }
  }, [loadDetail, onToast]);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  useEffect(() => {
    if (!availableBots.length) {
      setSelectedBotId(null);
      return;
    }
    if (!selectedBotId || !availableBots.some((bot) => bot.wechatRecordId === selectedBotId)) {
      setSelectedBotId(availableBots[0]?.wechatRecordId ?? null);
    }
  }, [availableBots, selectedBotId]);

  const resetPreview = useCallback(() => {
    setPreview(null);
    setRequestId(crypto.randomUUID());
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleBotChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const nextValue = Number.parseInt(event.target.value, 10);
    setSelectedBotId(Number.isInteger(nextValue) ? nextValue : null);
    setPreview(null);
  }, []);

  const handleFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] ?? null;
    if (!nextFile) {
      setFile(null);
      setPreview(null);
      return;
    }
    if (!nextFile.name.toLowerCase().endsWith('.xlsx')) {
      onToast('只支持 .xlsx 文件', 'error');
      event.target.value = '';
      setFile(null);
      setPreview(null);
      return;
    }
    if (nextFile.size > MAX_FILE_BYTES) {
      onToast('文件不能超过 5MB', 'error');
      event.target.value = '';
      setFile(null);
      setPreview(null);
      return;
    }
    setFile(nextFile);
    setPreview(null);
  }, [onToast]);

  const handlePreview = useCallback(async () => {
    if (!selectedBotId) {
      onToast('请选择目标工作号', 'error');
      return;
    }
    if (!file) {
      onToast('请选择 .xlsx 文件', 'error');
      return;
    }
    setLoadingPreview(true);
    try {
      const result = await previewInfluencerImport(file, selectedBotId);
      setPreview(result);
      setRequestId(crypto.randomUUID());
      onToast(`预览完成：${result.totalRows} 行，${result.pendingRows} 行待加好友`, 'success');
    } catch (error) {
      setPreview(null);
      onToast(error instanceof Error ? error.message : '预览失败', 'error');
    } finally {
      setLoadingPreview(false);
    }
  }, [file, onToast, selectedBotId]);

  const handleExecute = useCallback(async () => {
    if (!preview || executeBlockedReason) return;
    setExecuting(true);
    try {
      const detail = await executeInfluencerImport({
        requestId,
        filename: preview.filename,
        botWechatRecordId: preview.botWechatRecordId,
        message: message.trim(),
        rows: preview.rows,
      });
      setConfirmOpen(false);
      setSelectedBatchId(detail.batch.id);
      setSelectedBatchDetail(detail);
      await loadHistory(detail.batch.id);
      resetPreview();
      onToast(
        `导入已提交：${detail.batch.friendRows} 行直接打标签，${detail.batch.pendingRows} 行待加好友`,
        'success',
      );
    } catch (error) {
      onToast(error instanceof Error ? error.message : '导入执行失败', 'error');
    } finally {
      setExecuting(false);
    }
  }, [executeBlockedReason, loadHistory, message, onToast, preview, requestId, resetPreview]);

  const handleSelectBatch = useCallback((batchId: number) => {
    setSelectedBatchId(batchId);
    void loadDetail(batchId);
  }, [loadDetail]);

  if (booting) {
    return <main className="influencer-import-shell"><div className="empty-state">加载达人导入控制台...</div></main>;
  }

  return (
    <main className="influencer-import-shell">
      <header className="influencer-import-header">
        <div>
          <h1>达人导入</h1>
          <p>上传 Excel，预览好友命中与待加好友列表，再确认创建 Apollo 标签和加好友任务。</p>
        </div>
        <div className="influencer-import-header-actions">
          <button className="button" type="button" onClick={() => void loadInitial()} disabled={booting || loadingHistory || loadingDetail}>
            刷新工作号与历史
          </button>
        </div>
      </header>

      <section className="influencer-import-workspace">
        <section className="influencer-import-panel influencer-import-form-panel">
          <div className="influencer-import-panel-title">
            <h2>上传与预览</h2>
            <span>{selectedBot ? `目标：${formatBotLabel(selectedBot)}` : '先选择工作号'}</span>
          </div>

          <div className="influencer-import-form-grid">
            <label>
              <span>目标工作号</span>
              <select value={selectedBotId ?? ''} onChange={handleBotChange}>
                <option value="">请选择工作号</option>
                {availableBots.map((bot) => (
                  <option key={bot.wechatRecordId} value={bot.wechatRecordId}>
                    {formatBotLabel(bot)}
                  </option>
                ))}
              </select>
            </label>

            <label className="influencer-import-message-field">
              <span>验证语</span>
              <input
                type="text"
                maxLength={100}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="填写 1-100 个字符的加好友验证语"
              />
              <small>{message.trim().length}/100</small>
            </label>

            <label className="influencer-import-upload-field">
              <span>Excel 文件</span>
              <input
                ref={fileInputRef}
                type="file"
                accept={XLSX_ACCEPT}
                onChange={handleFileChange}
              />
              <div className="influencer-import-upload-meta">
                <strong>{file?.name ?? '未选择文件'}</strong>
                <span>仅支持 `.xlsx`，最大 5MB，最多 500 行。</span>
              </div>
            </label>
          </div>

          <div className="influencer-import-actions">
            <button className="button primary" type="button" onClick={() => void handlePreview()} disabled={!selectedBotId || !file || loadingPreview || executing}>
              {loadingPreview ? '解析中...' : '生成预览'}
            </button>
            <button className="button" type="button" onClick={resetPreview} disabled={!file && !preview}>
              清空文件
            </button>
            <button
              className="button"
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={Boolean(executeBlockedReason) || executing}
              title={executeBlockedReason ?? undefined}
            >
              确认导入
            </button>
          </div>

          {executeBlockedReason ? <div className="influencer-import-hint">{executeBlockedReason}</div> : null}

          <div className="influencer-import-stat-grid">
            <StatTile label="总行数" value={preview?.totalRows ?? 0} />
            <StatTile label="可处理" value={preview?.validRows ?? 0} />
            <StatTile label="已是好友" value={preview?.friendRows ?? 0} />
            <StatTile label="待加好友" value={preview?.pendingRows ?? 0} />
            <StatTile label="错误行" value={preview?.failedRows ?? 0} tone={preview?.failedRows ? 'danger' : 'default'} />
          </div>

          <div className="influencer-import-tag-strip">
            {preview?.tags.length ? preview.tags.map((tag) => (
              <span key={tag} className="influencer-import-tag">{tag}</span>
            )) : <span className="influencer-import-muted">预览后会在这里展示将创建或复用的系统标签。</span>}
          </div>
        </section>

        <section className="influencer-import-panel influencer-import-preview-panel">
          <div className="influencer-import-panel-title">
            <h2>预览明细</h2>
            <span>{preview ? `${preview.filename} · Bot #${preview.botWechatRecordId}` : '等待上传 Excel'}</span>
          </div>
          {loadingPreview ? (
            <div className="empty-state small">正在解析 Excel 并查询现有好友...</div>
          ) : preview ? (
            <PreviewTable rows={preview.rows} />
          ) : (
            <div className="empty-state small">
              <div className="empty-illustration">X</div>
              选择工作号和 Excel 后生成预览。错误行会被标红，但不会阻塞其他行执行。
            </div>
          )}
        </section>
      </section>

      <section className="influencer-import-history-grid">
        <section className="influencer-import-panel influencer-import-history-panel">
          <div className="influencer-import-panel-title">
            <h2>导入历史</h2>
            <span>{loadingHistory ? '刷新中...' : `${history.length} 个批次`}</span>
          </div>
          {history.length === 0 ? (
            <div className="empty-state small">还没有达人导入批次。</div>
          ) : (
            <div className="influencer-import-history-list">
              {history.map((batch) => (
                <button
                  key={batch.id}
                  type="button"
                  className={`influencer-history-item ${selectedBatchId === batch.id ? 'active' : ''}`}
                  onClick={() => handleSelectBatch(batch.id)}
                >
                  <div className="influencer-history-item-main">
                    <strong>{batch.filename}</strong>
                    <span>#{batch.id} · Bot {batch.botWechatRecordId}</span>
                  </div>
                  <StatusBadge status={batch.status} />
                  <div className="influencer-history-item-stats">
                    <span>好友 {batch.friendRows}</span>
                    <span>待加 {batch.pendingRows}</span>
                    <span>失败 {batch.failedRows}</span>
                    <span>{formatDateTime(batch.createdAt)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="influencer-import-panel influencer-import-detail-panel">
          <div className="influencer-import-panel-title">
            <h2>批次详情</h2>
            <span>{selectedBatchDetail ? `批次 #${selectedBatchDetail.batch.id}` : '选择左侧批次查看明细'}</span>
          </div>
          {loadingDetail ? (
            <div className="empty-state small">正在加载批次详情...</div>
          ) : selectedBatchDetail ? (
            <>
              <div className="influencer-import-detail-summary">
                <StatTile label="总行数" value={selectedBatchDetail.batch.totalRows} compact />
                <StatTile label="已是好友" value={selectedBatchDetail.batch.friendRows} compact />
                <StatTile label="待加好友" value={selectedBatchDetail.batch.pendingRows} compact />
                <StatTile label="失败" value={selectedBatchDetail.batch.failedRows} compact tone={selectedBatchDetail.batch.failedRows ? 'danger' : 'default'} />
              </div>
              <DetailTable mappings={selectedBatchDetail.mappings} />
            </>
          ) : (
            <div className="empty-state small">选择一个历史批次查看逐行状态、Apollo 任务号和错误信息。</div>
          )}
        </section>
      </section>

      {confirmOpen && preview ? (
        <div className="influencer-import-modal-backdrop" role="presentation">
          <div className="influencer-import-modal" role="dialog" aria-modal="true" aria-labelledby="influencer-import-confirm-title">
            <h2 id="influencer-import-confirm-title">确认导入达人批次</h2>
            <p>
              本次将为 <strong>{preview.friendRows}</strong> 行已是好友数据直接写系统标签，
              为 <strong>{preview.pendingRows}</strong> 行创建加好友任务。
              {preview.failedRows > 0 ? ` 另外有 ${preview.failedRows} 行错误会跳过。` : ''}
            </p>

            <div className="influencer-import-confirm-block">
              <h3>将写入的标签</h3>
              <div className="influencer-import-tag-strip">
                {preview.tags.map((tag) => <span key={tag} className="influencer-import-tag">{tag}</span>)}
              </div>
            </div>

            <div className="influencer-import-confirm-block">
              <h3>待加好友微信号（精确列表）</h3>
              {pendingWechatIds.length > 0 ? (
                <div className="influencer-import-wechat-list">
                  {pendingWechatIds.map((wechatId) => <span key={wechatId}>{wechatId}</span>)}
                </div>
              ) : (
                <div className="influencer-import-muted">本次预览里没有待加好友行。</div>
              )}
            </div>

            <div className="influencer-import-modal-actions">
              <button className="button" type="button" onClick={() => setConfirmOpen(false)} disabled={executing}>取消</button>
              <button className="button primary" type="button" onClick={() => void handleExecute()} disabled={executing}>
                {executing ? '导入中...' : '确认导入'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}

function PreviewTable({ rows }: { rows: InfluencerPreviewRow[] }) {
  return (
    <div className="influencer-table">
      <div className="influencer-table-row header">
        <span>行</span>
        <span>达人</span>
        <span>微信 / 手机</span>
        <span>标签</span>
        <span>状态</span>
        <span>错误</span>
      </div>
      {rows.map((row) => {
        const rowStatus = previewRowStatus(row);
        return (
          <div key={`${row.rowNumber}-${row.wechatId}`} className={`influencer-table-row ${rowStatus}`}>
            <span>#{row.rowNumber}</span>
            <div className="influencer-table-primary">
              <strong>{row.nickname || '-'}</strong>
              <small>{row.platform} · {row.platformUserId || '缺少 ID'}</small>
            </div>
            <div className="influencer-table-primary">
              <strong>{row.wechatId || '-'}</strong>
              <small>{row.phone || '无手机号'}</small>
            </div>
            <div className="influencer-import-tag-strip dense">
              {row.tags.map((tag) => <span key={`${row.rowNumber}-${tag}`} className="influencer-import-tag">{tag}</span>)}
            </div>
            <span><StatusBadge status={rowStatus === 'friend' ? 'mapped' : rowStatus === 'pending' ? 'pending_friend' : 'failed'} /></span>
            <span className="influencer-table-errors">
              {row.errors.length ? row.errors.join('；') : row.friendId ? `已命中好友 #${row.friendId}` : '将创建加好友任务'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function DetailTable({ mappings }: { mappings: InfluencerFriendMapping[] }) {
  return (
    <div className="influencer-table">
      <div className="influencer-table-row header detail">
        <span>微信</span>
        <span>达人</span>
        <span>状态</span>
        <span>Friend / Task</span>
        <span>标签</span>
        <span>错误</span>
      </div>
      {mappings.map((mapping) => (
        <div key={mapping.id} className={`influencer-table-row detail ${mapping.status}`}>
          <div className="influencer-table-primary">
            <strong>{mapping.wechatId}</strong>
            <small>{mapping.phone || '无手机号'}</small>
          </div>
          <div className="influencer-table-primary">
            <strong>{mapping.nickname || mapping.platformUserId}</strong>
            <small>{mapping.platform} · {mapping.platformUserId}</small>
          </div>
          <span><StatusBadge status={mapping.status} /></span>
          <div className="influencer-table-primary">
            <strong>{mapping.friendId ? `好友 #${mapping.friendId}` : '未映射好友'}</strong>
            <small>{mapping.apolloTaskId ? `任务 #${mapping.apolloTaskId}` : '无任务号'}</small>
          </div>
          <div className="influencer-import-tag-strip dense">
            {mapping.tags.map((tag) => <span key={`${mapping.id}-${tag}`} className="influencer-import-tag">{tag}</span>)}
          </div>
          <span className="influencer-table-errors">{mapping.error || '—'}</span>
        </div>
      ))}
    </div>
  );
}

function StatTile({
  label,
  value,
  compact = false,
  tone = 'default',
}: {
  label: string;
  value: string | number;
  compact?: boolean;
  tone?: 'default' | 'danger';
}) {
  return (
    <div className={`influencer-stat-tile ${compact ? 'compact' : ''} ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function StatusBadge({ status }: { status: InfluencerImportBatch['status'] | InfluencerMappingStatus }) {
  return <span className={`influencer-status ${status}`}>{statusLabel(status)}</span>;
}

function previewRowStatus(row: InfluencerPreviewRow): 'failed' | 'friend' | 'pending' {
  if (row.errors.length > 0) return 'failed';
  if (row.friendId) return 'friend';
  return 'pending';
}

function statusLabel(status: InfluencerImportBatch['status'] | InfluencerMappingStatus): string {
  const labels: Record<string, string> = {
    parsed: '已解析',
    processing: '处理中',
    completed: '已完成',
    failed: '失败',
    pending_friend: '待加好友',
    mapped: '已命中好友',
    tagged: '已打标签',
  };
  return labels[status] ?? status;
}

function formatBotLabel(bot: EnvironmentRouteAccount): string {
  const name = bot.nickname || bot.wechatId || '未命名';
  return `${name} (${bot.wechatRecordId})`;
}

function formatDateTime(value: string | null): string {
  if (!value) return '刚刚';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function unique(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}
